import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { Button } from '../components/ui/button';
import { FileText, Plus, Clock, CheckCircle, AlertCircle, CreditCard, Loader2, Database, Activity, Car, Users, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { WorkCard, WorkModal } from '../components/ShowcaseCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending_review: { label: 'Pendiente', color: 'text-yellow-500 bg-yellow-500/10' },
  needs_info: { label: 'Falta info', color: 'text-orange-500 bg-orange-500/10' },
  accepted: { label: 'Aceptada', color: 'text-blue-500 bg-blue-500/10' },
  in_progress: { label: 'En proceso', color: 'text-purple-500 bg-purple-500/10' },
  delivered: { label: 'Entregada', color: 'text-green-500 bg-green-500/10' },
  closed: { label: 'Cerrada', color: 'text-zinc-500 bg-zinc-500/10' },
  cancelled: { label: 'Cancelada', color: 'text-red-500 bg-red-500/10' },
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashStats, setDashStats] = useState({ queue_count: 0, total_solutions: 0, recent_solutions: [] });
  const [works, setWorks] = useState([]);
  const [selectedWork, setSelectedWork] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTickets(r.data.tickets))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDashStats(r.data))
      .catch(() => {});
    axios.get(`${API}/showcase`)
      .then(r => setWorks(r.data.works || []))
      .catch(() => {});
  }, [token]);

  // Refresh dashboard stats every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setDashStats(r.data))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const counts = {
    active: tickets.filter(t => ['pending_review', 'needs_info', 'accepted', 'in_progress'].includes(t.status)).length,
    delivered: tickets.filter(t => t.status === 'delivered').length,
    total: tickets.length,
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="user-dashboard">
        <EmailVerificationBanner />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">{t('BIENVENIDO')}, {user?.company_name?.toUpperCase()}</h1>
            <p className="text-zinc-400 mt-1">{t('Panel de control de tus solicitudes')}</p>
          </div>
          <Link to="/tickets/new">
            <Button className="bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider" data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('Nueva Solicitud')}
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-surface border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-500" />
              <div><p className="text-zinc-400 text-sm">{t('Activas')}</p><p className="text-2xl font-mono font-bold text-white">{counts.active}</p></div>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div><p className="text-zinc-400 text-sm">{t('Entregadas')}</p><p className="text-2xl font-mono font-bold text-white">{counts.delivered}</p></div>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-500" />
              <div><p className="text-zinc-400 text-sm">{t('Total')}</p><p className="text-2xl font-mono font-bold text-white">{counts.total}</p></div>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-primary" />
              <div><p className="text-zinc-400 text-sm">{t('Créditos')}</p><p className="text-2xl font-mono font-bold text-primary">{user?.credits === -1 ? String.fromCharCode(8734) : (user?.credits || 0)}</p></div>
            </div>
          </div>
          <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="admins-online">
            <div className="flex items-center gap-3">
              <Users className={`w-8 h-8 ${dashStats?.online_admins?.length > 0 ? 'text-emerald-500' : 'text-red-500'}`} />
              <div>
                <p className="text-zinc-400 text-sm">{t('Soporte Online')}</p>
                {dashStats?.online_admins?.length > 0 ? (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                    <span className="text-emerald-400 text-sm font-medium">{t('Conectado')} - {dashStats.online_admins.map(a => a.name).join(', ')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="text-red-400 text-sm">{t('Desconectado')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live Monitors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Queue Monitor */}
          <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="queue-monitor">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-yellow-500" />
              <h3 className="text-zinc-400 text-sm font-medium">{t('Cola de Archivos')}</h3>
              <span className="ml-auto w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Actualización en tiempo real" />
            </div>
            <p className="font-mono text-3xl font-bold text-white">{dashStats.queue_count}</p>
            <p className="text-zinc-500 text-xs mt-1">{t('archivos en proceso')}</p>
          </div>

          {/* Total Solutions Monitor */}
          <div className="bg-surface border border-white/10 rounded-md p-4" data-testid="solutions-monitor">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-blue-500" />
              <h3 className="text-zinc-400 text-sm font-medium">{t('Base de Datos')}</h3>
              <span className="ml-auto w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Actualización en tiempo real" />
            </div>
            <p className="font-mono text-3xl font-bold text-white">{dashStats.total_solutions}</p>
            <p className="text-zinc-500 text-xs mt-1">{t('soluciones disponibles')}</p>
          </div>

          {/* Créditos */}
          <div className="bg-surface border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-primary" />
              <h3 className="text-zinc-400 text-sm font-medium">{t('Mi Saldo')}</h3>
            </div>
            <p className="font-mono text-3xl font-bold text-primary">{user?.credits === -1 ? String.fromCharCode(8734) : (user?.credits || 0)}</p>
            <p className="text-zinc-500 text-xs mt-1">{t('créditos disponibles')}</p>
          </div>
        </div>

        {/* Recent Solutions Feed */}
        <div className="bg-surface border border-white/10 rounded-md" data-testid="recent-solutions-feed">
          <div className="p-4 border-b border-white/10 flex items-center gap-2">
            <Car className="w-4 h-4 text-green-500" />
            <h2 className="font-heading text-lg font-bold text-white">{t('ÚLTIMAS SOLUCIONES APLICADAS')}</h2>
            <span className="ml-auto w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          </div>
          {dashStats.recent_solutions?.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">{t('No hay soluciones recientes')}</div>
          ) : (
            <div className="divide-y divide-white/5">
              {dashStats.recent_solutions?.map((sol, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-mono font-bold ${sol.vehicle_type === 'industrial' ? 'bg-orange-500/10 text-orange-400' : 'bg-blue-500/10 text-blue-400'}`}>
                    {sol.vehicle_type === 'industrial' ? 'IND' : 'TUR'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {sol.vehicle_brand} {sol.vehicle_model} {sol.vehicle_year && `(${sol.vehicle_year})`}
                    </p>
                    <p className="text-zinc-500 text-xs truncate">{sol.service_name}</p>
                  </div>
                  <span className="text-zinc-600 text-xs flex-shrink-0">
                    {sol.updated_at ? new Date(sol.updated_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Tickets */}
        <div className="bg-surface border border-white/10 rounded-md">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-white">{t('SOLICITUDES RECIENTES')}</h2>
            <Link to="/tickets" className="text-primary text-sm hover:underline">{t('Ver todas')}</Link>
          </div>
          {tickets.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">{t('No tienes solicitudes aún')}</p>
              <Link to="/tickets/new"><Button className="mt-3 bg-primary hover:bg-primary/90" size="sm">{t('Crear primera solicitud')}</Button></Link>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {tickets.slice(0, 5).map(tk => {
                const s = STATUS_MAP[tk.status] || STATUS_MAP.pending_review;
                return (
                  <Link key={tk.id} to={`/tickets/${tk.id}`} className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors" data-testid={`ticket-row-${tk.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-500">{tk.ticket_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${s.color}`}>{t(s.label)}</span>
                      </div>
                      <p className="text-white text-sm mt-1 truncate">{tk.vehicle_brand} {tk.vehicle_model} - {tk.service_name}</p>
                    </div>
                    <span className="text-zinc-600 text-xs">{new Date(tk.created_at).toLocaleDateString('es-ES')}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Trabajos Realizados */}
        {works.length > 0 && (
          <div data-testid="dashboard-showcase">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-lg font-bold text-white">{t('TRABAJOS REALIZADOS')}</h2>
              </div>
              <Link to="/trabajos" className="text-primary text-sm hover:underline">{t('Ver todos')}</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {works.slice(0, 6).map(w => (
                <WorkCard key={w.id} work={w} onClick={() => setSelectedWork(w)} />
              ))}
            </div>
          </div>
        )}
      </div>
      <WorkModal work={selectedWork} onClose={() => setSelectedWork(null)} />
    </Layout>
  );
};

export default Dashboard;
